import { observer } from "mobx-react-lite";
import { useMemo, useEffect } from "react";
import { VirtualizedItemList } from "@/components/item-list";
import {
  ItemListRowData,
  ItemListRowType,
  ItemListItemRowData,
} from "@/components/item-list/types";
import { ItemListItemKind, ItemListItem } from "@/components/item-list/types/item-list-item-types";
import { MDSRowItemAppearance } from "@/mds";
import { AppStore, useAppStore } from "@/store";
import { isMac } from "@/domains/platform/isMac";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { useAsync } from "@/modules/use-async";
import { getItemListRowForNote, getItemListRowForCollection } from "@/components/item-list/helpers";
import { generateShortDateString } from "@/domains/date/date";
import { DateTime } from "luxon";
import { itemsModule } from "@/modules/items";

export interface SuggestedSearchesListProps {}

const questionWords = ["who", "what", "when", "where", "why", "how", "which", "whose", "whom"];

export const SuggestedSearchesList = observer<SuggestedSearchesListProps>(
  function SuggestedSearchesList() {
    const { store, pageStore } = useAppStore();
    const state = pageStore.quickSearchModal;
    const currentSearchQuery = state.value;
    const onClick = state.close;
    const limit = 30;

    const { handleSearch, handleAsk, listState } = pageStore.quickSearchModal;

    const { result, invoke } = useAsync({
      fn: async () => await store.search.forSuggestions(state.value),
      options: {
        defaultValue: [],
      },
    });

    // safe to cast since we set defaultValue
    const suggestions = result as SearchSuggestion[];
    useEffect(() => {
      invoke(state.value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.value]);

    const fixedItems: ItemListItem[] = useMemo(() => {
      const fixedItems: ItemListItem[] = [
        {
          id: `search-${currentSearchQuery}`,
          kind: ItemListItemKind.Search,
          appearance: MDSRowItemAppearance.Standard,
          label: `Search for "${currentSearchQuery}"`,
          onClick: ({ event }) =>
            getClickHandler(
              null,
              store,
              handleSearch,
              onClick
            )({
              event: event as
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLButtonElement>,
            }),
        },
      ];

      const lowercaseQuery = currentSearchQuery.toLowerCase();
      const isQuestion =
        lowercaseQuery.includes("?") || questionWords.some(word => lowercaseQuery.includes(word));
      if (isQuestion) {
        fixedItems.unshift({
          id: `ask-${currentSearchQuery}`,
          kind: ItemListItemKind.Message,
          appearance: MDSRowItemAppearance.Standard,
          label: `Ask "${currentSearchQuery}"`,
          onClick: ({ event }) =>
            getClickHandler(
              null,
              store,
              handleAsk,
              onClick
            )({
              event: event as
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLButtonElement>,
            }),
        });
      }

      return fixedItems;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSearchQuery, handleAsk, onClick, store]);

    const itemRows: ItemListRowData[] = useMemo(() => {
      const filteredItems = suggestions
        .filter(
          item =>
            item.type === SearchSuggestionType.NOTE || item.type === SearchSuggestionType.COLLECTION
        )
        .slice(0, limit);

      // Register all item IDs in the ordered list for keyboard navigation
      const allItemIds = [
        ...fixedItems.map(item => item.id),
        ...filteredItems.map(item => item.modelId),
      ];
      const isCollection = [
        ...Array.from({ length: fixedItems.length }, () => false),
        ...filteredItems.map(item => item.type === SearchSuggestionType.COLLECTION),
      ];
      state.setOrderedItemIds(allItemIds, isCollection);

      const rows: ItemListRowData[] = [
        {
          key: "header",
          type: ItemListRowType.SectionHeader,
          payload: { title: "Suggestions" },
        },
        ...fixedItems.map(item => {
          const mdsItem: ItemListItem = {
            ...item,
            listState,
          };

          const rowData: ItemListItemRowData = {
            type: ItemListRowType.Item,
            key: item.id,
            payload: mdsItem,
          };

          return rowData;
        }),
        ...filteredItems.map(item => {
          if (item.type === SearchSuggestionType.NOTE) {
            const itemId = item.modelId;
            const itemAppearance = itemsModule.resolveAppearance({ itemId, appStore: store });

            const mdsItem: ItemListRowData = {
              type: ItemListRowType.AsyncNote,
              key: item.modelId,
              payload: {
                noteId: item.modelId,
                itemRow: note =>
                  getItemListRowForNote({
                    dateTime: item.lastViewedAt || "",
                    inMainPanel: true,
                    noteObservable: note,
                    listState: state.listState,
                    store,
                    disablePreview: true,
                    trackClick: onClick,
                    appearance: itemAppearance,
                  }),
              },
            };
            return mdsItem;
          }

          const dateLabel = generateShortDateString(
            typeof item.lastViewedAt === "string"
              ? DateTime.fromISO(item.lastViewedAt)
              : DateTime.fromJSDate(item.lastViewedAt)
          );
          const mdsItem: ItemListRowData = {
            type: ItemListRowType.AsyncCollection,
            key: item.modelId,
            payload: {
              collectionId: item.modelId,
              itemRow: collection =>
                getItemListRowForCollection({
                  dateLabel,
                  collectionObservable: collection,
                  listState: state.listState,
                  store,
                  disablePreview: true,
                  trackClick: onClick,
                }),
            },
          };
          return mdsItem;
        }),
      ];

      return rows;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      currentSearchQuery,
      limit,
      suggestions,
      fixedItems,
      listState,
      state,
      store,
      handleSearch,
      onClick,
    ]);

    return (
      <VirtualizedItemList
        rows={itemRows}
        listState={state.listState}
        scrollHighlightedItemIntoView
      />
    );
  }
);

// TODO: following "Recents" pattern here but we can re-do Dropdown as set of links and allow browser to take care of opening new page natively
const getClickHandler =
  (
    item: SearchSuggestion | null,
    store: AppStore,
    handleSearch: () => void,
    onClick?: () => void
  ) =>
  ({
    event,
  }: {
    event?: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>;
  }) => {
    if (!item) {
      if (isMac() ? event?.metaKey : event?.ctrlKey) {
        handleSearch();
      } else {
        handleSearch();
        onClick?.();
      }

      return;
    }

    if (item.type === SearchSuggestionType.NOTE) {
      store.navigation.goToNote({ noteId: item.modelId, event });
      onClick?.();
    }
    if (item.type === SearchSuggestionType.COLLECTION) {
      store.navigation.goToCollection({ collectionId: item.modelId, event });
      onClick?.();
    }
  };
